import React from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Card,
  CardContent,
  EmptyState,
  Flex,
  Heading,
  LoadingOrbit,
  Page,
  PageHeader,
  TagBadge,
  Text,
  useToast,
} from '@chatsdet/ui';
import { Camera } from '@phosphor-icons/react';
import type { VisualCheckpointDetail } from '@chatsdet/types';
import { ApiError } from '../../../lib/api-error';
import {
  useMarkVisualRegression,
  useRevertVisualMask,
  useVisualCheckpoint,
} from '../../../features/visual/useVisual';
import {
  intentPresentation,
  outcomePresentation,
  verdictPresentation,
} from '../../../features/visual/visualPresentation';
import { VisualImageViewer } from './VisualImageViewer';

function SourceLinks({ detail }: { detail: VisualCheckpointDetail }) {
  const linkClass = 'font-medium text-foreground hover:underline';
  return (
    <Flex align="center" gap="sm" wrap>
      {detail.source === 'native' ? (
        <>
          {detail.workflowId && (
            <Link to="/flows/$workflowId" params={{ workflowId: detail.workflowId }} className={linkClass}>
              {detail.workflowName ?? 'Flow'}
            </Link>
          )}
          {detail.workflowId && detail.executionId && (
            <span aria-hidden className="text-muted-foreground">
              ·
            </span>
          )}
          {detail.executionId && (
            <Link to="/runs/test/$executionId" params={{ executionId: detail.executionId }} className={linkClass}>
              Test run
            </Link>
          )}
        </>
      ) : (
        <>
          {detail.externalTestRunId && (
            <Link to="/runs/external/$runId" params={{ runId: detail.externalTestRunId }} className={linkClass}>
              External run
            </Link>
          )}
          <Text size="sm" className="text-muted-foreground">
            {[detail.specFile, detail.testTitle].filter(Boolean).join(' · ')}
          </Text>
        </>
      )}
    </Flex>
  );
}

function HealedMaskCard({ detail }: { detail: VisualCheckpointDetail }) {
  const { toast } = useToast();
  const revert = useRevertVisualMask(detail.id);
  const mask = detail.healedMask;
  if (!mask) return null;
  return (
    <Card>
      <CardContent>
        <Flex align="center" justify="between" gap="md" wrap>
          <Text size="sm">
            healed: masked {mask.description} because {mask.reason}
          </Text>
          {detail.source === 'native' && (
            <Button
              variant="outline"
              size="sm"
              isDisabled={revert.isPending || detail.outcome === null}
              onPress={async () => {
                try {
                  await revert.mutateAsync(mask.id);
                  toast.success('Mask reverted. Re-comparing this checkpoint.');
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to revert mask');
                }
              }}
            >
              Revert
            </Button>
          )}
        </Flex>
      </CardContent>
    </Card>
  );
}

function ExplanationSection({ detail }: { detail: VisualCheckpointDetail }) {
  const explanation = detail.explanation;
  if (!explanation) return null;
  const verdict = verdictPresentation(explanation.verdict);
  const intent = intentPresentation(explanation.intent);
  return (
    <section className="space-y-2">
      <Heading level={3}>Explanation</Heading>
      <Flex align="center" gap="sm" wrap>
        <TagBadge name={verdict.label} color={verdict.color} size="md" />
        <TagBadge name={intent.label} color={intent.color} size="md" />
      </Flex>
      <Text size="sm">{explanation.text}</Text>
    </section>
  );
}

function TextChangesSection({ detail }: { detail: VisualCheckpointDetail }) {
  const changes = detail.textChanges?.changes ?? [];
  if (changes.length === 0) return null;
  return (
    <section className="space-y-2">
      <Heading level={3}>Text changes</Heading>
      <ul className="space-y-1">
        {changes.map((change, i) => (
          <li key={i}>
            <Text size="sm">
              <span className="text-muted-foreground">{change.kind}: </span>
              {change.before && <span className="line-through">{change.before.text}</span>}
              {change.before && change.after && ' → '}
              {change.after && <span>{change.after.text}</span>}
            </Text>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function VisualCheckpointPage() {
  const { captureId } = useParams({ from: '/visual/$captureId' });
  const { toast } = useToast();
  const query = useVisualCheckpoint(captureId);
  const regression = useMarkVisualRegression(captureId);

  if (query.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }
  const detail = query.data;
  if (!detail && query.isError && !(query.error instanceof ApiError && query.error.status === 404)) {
    return (
      <Page>
        <EmptyState
          icon={<Camera size={48} />}
          title="Could not load checkpoint"
          description={query.error.message}
          action={
            <Button variant="outline" onPress={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      </Page>
    );
  }
  if (!detail) {
    return (
      <Page>
        <EmptyState
          icon={<Camera size={48} />}
          title="Checkpoint not found"
          description="It may have been deleted, or belongs to another organization."
          action={
            <Link to="/visual" search={{ status: 'changed' }}>
              <Button variant="outline">Back to Visual</Button>
            </Link>
          }
        />
      </Page>
    );
  }

  const outcome = outcomePresentation(detail.outcome);
  const isMarked = detail.regressionMarkedAt !== null;

  return (
    <Page>
      <PageHeader
        icon={<Camera size={24} weight="fill" />}
        eyebrow={<TagBadge name={outcome.label} color={outcome.color} size="md" />}
        title={detail.label}
        description={
          <Flex align="center" gap="sm" wrap>
            <SourceLinks detail={detail} />
            <Text size="sm" className="text-muted-foreground">
              {detail.environmentName}
              {detail.severity ? ` · severity ${detail.severity}` : ''}
              {detail.changedPercent !== null ? ` · ${detail.changedPercent.toFixed(2)}% changed` : ''}
            </Text>
          </Flex>
        }
        actions={
          <Button
            variant={isMarked ? 'outline' : 'primary'}
            size="sm"
            isDisabled={regression.isPending}
            onPress={async () => {
              try {
                await regression.mutateAsync(!isMarked);
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed to update regression mark');
              }
            }}
          >
            {isMarked ? 'Clear regression' : 'Mark as regression'}
          </Button>
        }
      />
      <div className="space-y-6">
        {detail.outcome === null && <Banner variant="info">Re-comparing this checkpoint. Results will appear shortly.</Banner>}
        {(detail.outcome === 'unstable' || detail.outcome === 'cant_compare') && detail.outcomeReason && (
          <Banner variant="warning">{detail.outcomeReason}</Banner>
        )}
        {isMarked && <Banner variant="error">Marked as a regression.</Banner>}
        {detail.maskMisses.length > 0 && (
          <Banner variant="warning">
            <ul>
              {detail.maskMisses.map((miss) => (
                <li key={miss}>Could not find masked element: {miss}</li>
              ))}
            </ul>
          </Banner>
        )}
        <HealedMaskCard detail={detail} />
        <ExplanationSection detail={detail} />
        <VisualImageViewer detail={detail} />
        <TextChangesSection detail={detail} />
      </div>
    </Page>
  );
}
